import { useState } from "react";
import { Building2, ChevronsUpDown, Check } from "lucide-react";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from "@/components/ui/command";

export interface CompanyOption {
  value: string;
  label: string;
}

interface CompanySwitcherProps {
  companies: CompanyOption[];
  selectedCompany: string;
  onCompanyChange: (company: string) => void;
  label?: string;
}

/**
 * Company Switcher Component
 */
export function CompanySwitcher({
  companies,
  selectedCompany,
  onCompanyChange,
  label = "Company",
}: CompanySwitcherProps) {
  const [open, setOpen] = useState(false);

  const selectedCo = companies.find((co) => co.value === selectedCompany);

  return (
    <div className="group-data-[state=collapsed]:hidden">
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <button className="flex items-center gap-2 w-full hover:bg-teal-50 rounded-md p-2 transition-colors">
            <div className="w-8 h-8 rounded-full bg-teal-100 flex items-center justify-center">
              <Building2 className="h-4 w-4 text-teal-600" />
            </div>
            <div className="flex-1 text-left text-sm leading-tight">
              <div className="font-medium text-neutral-700">
                {selectedCo?.label || "Select..."}
              </div>
              <div className="text-xs text-muted-foreground">{label}</div>
            </div>
            <ChevronsUpDown className="h-4 w-4 text-muted-foreground" />
          </button>
        </PopoverTrigger>
        <PopoverContent side="top" align="start" className="w-[240px] p-0">
          <Command>
            <CommandInput placeholder={`Search ${label.toLowerCase()}...`} />
            <CommandList>
              <CommandEmpty>No {label.toLowerCase()} found.</CommandEmpty>
              <CommandGroup>
                {companies.map((company) => (
                  <CommandItem
                    key={company.value}
                    value={company.value}
                    onSelect={(value) => {
                      onCompanyChange(value);
                      setOpen(false);
                    }}
                  >
                    {company.label}
                    {selectedCompany === company.value && (
                      <Check className="ml-auto h-4 w-4 text-teal-600" />
                    )}
                  </CommandItem>
                ))}
              </CommandGroup>
            </CommandList>
          </Command>
        </PopoverContent>
      </Popover>
    </div>
  );
}
